/**
 * MKS-203 — "Profile photo" ProfilePage card.
 *
 * Visual language matches the neighbouring `PersonalEmailSection` /
 * `MonkBotSection` cards (bg-white rounded-xl shadow-sm ...), not the ui/
 * kit used by PreferencesPage.
 *
 * Flow: pick a file (client-side type + 5 MB size check) -> crop/zoom in a
 * round preview (`react-easy-crop`) -> draw the cropped region onto a
 * 512x512 canvas -> upload the blob. The server re-validates type and size
 * and re-encodes everything through sharp regardless (T-203-07) — the
 * client-side checks only give a faster answer.
 */
import { useState, useRef, useCallback } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../ui/Avatar';
import { Button } from '../common/Button';
import { ConfirmModal } from '../common/Modal';
import { useAuth } from '../../services/authContext';
import { useUploadAvatar, useResetAvatar } from '../../hooks/useAvatar';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const OUTPUT_SIZE = 512;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the selected image'));
    img.src = src;
  });
}

async function cropToBlob(imageSrc: string, area: Area): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }
  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE,
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Could not encode the cropped image'));
      }
    }, 'image/png');
  });
}

export function AvatarSection() {
  const { user } = useAuth();
  const uploadMutation = useUploadAvatar();
  const resetMutation = useResetAvatar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [pickedImageSrc, setPickedImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const onCropComplete = useCallback((_croppedArea: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const closeCropModal = () => {
    if (pickedImageSrc) {
      URL.revokeObjectURL(pickedImageSrc);
    }
    setPickedImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setClientError(null);
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setClientError('Please choose a JPEG, PNG, or WebP image.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setClientError('That image is larger than 5 MB — please choose a smaller one.');
      e.target.value = '';
      return;
    }

    setPickedImageSrc(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!pickedImageSrc || !croppedAreaPixels) return;
    try {
      const blob = await cropToBlob(pickedImageSrc, croppedAreaPixels);
      await uploadMutation.mutateAsync(blob);
      closeCropModal();
    } catch {
      // Failure is already surfaced via the mutation's error toast
      // (uploadMutation.onError); a crop/draw failure (no network call made
      // yet) has no toast of its own, so fall through and leave the modal
      // open for the user to retry.
    }
  };

  const handleReset = () => {
    resetMutation.mutate(undefined, {
      onSuccess: () => setShowResetConfirm(false),
    });
  };

  if (!user) return null;

  const displayName =
    user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <PhotoIcon className="h-6 w-6 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">Profile photo</h2>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Synced from Slack or Google when available. Upload your own to override it — crop and
        zoom it into a circle below.
      </p>

      <div className="flex items-center gap-4">
        <Avatar name={displayName} colorSeq={user.avatarColorSeq} avatarUrl={user.avatarUrl} size="lg" />

        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            onChange={handleFileChange}
            className="text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
          />
          {user.hasAvatarUpload && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowResetConfirm(true)}
              disabled={resetMutation.isPending}
              className="self-start"
            >
              {resetMutation.isPending ? 'Resetting...' : 'Reset to Slack/Google photo'}
            </Button>
          )}
        </div>
      </div>

      {clientError && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {clientError}
        </p>
      )}

      {pickedImageSrc && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Crop your photo"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Crop your photo</h3>
            <div className="relative w-full h-64 bg-gray-900 rounded-lg overflow-hidden">
              <Cropper
                image={pickedImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="mt-4">
              <label htmlFor="avatar-zoom" className="block text-xs text-gray-500 mb-1">
                Zoom
              </label>
              <input
                id="avatar-zoom"
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full"
              />
            </div>
            {uploadMutation.isError && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                Couldn&apos;t upload your photo. Please try again.
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={closeCropModal} disabled={uploadMutation.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleSave()}
                disabled={uploadMutation.isPending || !croppedAreaPixels}
              >
                {uploadMutation.isPending ? 'Saving...' : 'Save photo'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleReset}
        title="Reset profile photo"
        message="This removes your uploaded photo and restores your synced Slack/Google photo (or initials, if you have none)."
        confirmText="Reset"
        isLoading={resetMutation.isPending}
      />
    </div>
  );
}
